import { Box, Button } from "@mui/material";
import useColors from "../../api/hooks/useColors";
import SvgToIcon from "../../icons/SvgToIcon";
import LoadingIcon from "../LoadingIcon";

export default function TextButton({
  onClick,
  color = "inherit",
  size = "small",
  disabled = false,
  loading = false,
  startIcon = null,
  endIcon = null,
  children,
  ...props
}) {
  const colors = useColors();

  const colorMap = {
    inherit: {
      text: colors.button_text_inherit_enabled_text,
      enabled: {
        bg: colors.button_text_inherit_enabled_bg,
      },
      hover: {
        bg: colors.button_text_inherit_hovered_bg,
      },
      disabled: {
        text: colors.button_text_disabled_text,
      },
    },
    primary: {
      text: colors.button_text_primary_enabled_text,
      enabled: {
        bg: colors.button_text_primary_enabled_bg,
      },
      hover: {
        bg: colors.button_text_primary_hovered_bg,
      },
      disabled: {
        text: colors.button_text_disabled_text,
      },
    },
    success: {
      text: colors.button_text_success_enabled_text,
      enabled: {
        bg: colors.button_text_success_enabled_bg,
      },
      hover: {
        bg: colors.button_text_success_hovered_bg,
      },
      disabled: {
        text: colors.button_text_disabled_text,
      },
    },
    error: {
      text: colors.button_text_error_enabled_text,
      enabled: {
        bg: colors.button_text_error_enabled_bg,
      },
      hover: {
        bg: colors.button_text_error_hovered_bg,
      },
      disabled: {
        text: colors.button_text_disabled_text,
      },
    },
  };

  const sizeMap = {
    xs: {
      fontSize: "12px",
      fontWeight: 400,
      height: "24px",
      paddingX: "4px",
      borderRadius: "10px",
      loadingSize: 6,
      iconSize: "16px",
    },
    small: {
      fontSize: "12px",
      fontWeight: 500,
      height: "32px",
      paddingX: "4px",
      borderRadius: "10px",
      loadingSize: 8,
      iconSize: "18px",
    },
    medium: {
      fontSize: "14px",
      fontWeight: 500,
      height: "36px",
      paddingX: "8px",
      borderRadius: "12px",
      loadingSize: 10,
      iconSize: "20px",
    },
    large: {
      fontSize: "14px",
      fontWeight: 500,
      height: "48px",
      paddingX: "10px",
      borderRadius: "14px",
      loadingSize: 12,
      iconSize: "20px",
    },
  };

  const colorConfig = colorMap[color];
  const sizeConfig = sizeMap[size];

  return (
    <Button
      variant="text"
      onClick={onClick}
      disabled={disabled || loading}
      disableRipple
      disableFocusRipple
      disableElevation
      startIcon={
        startIcon ? (
          <SvgToIcon
            IconLogo={startIcon}
            width={sizeConfig.iconSize}
            style={{ color: colorConfig.text }}
          />
        ) : null
      }
      endIcon={
        endIcon ? (
          <SvgToIcon
            IconLogo={endIcon}
            width={sizeConfig.iconSize}
            style={{
              color: disabled ? colorConfig.disabled.text : colorConfig.text,
            }}
          />
        ) : null
      }
      {...props}
      sx={{
        textTransform: "none",
        fontSize: sizeConfig.fontSize,
        fontWeight: sizeConfig.fontWeight,
        width: "fit-content",
        height: sizeConfig.height,
        paddingX: sizeConfig.paddingX,
        borderRadius: sizeConfig.borderRadius,
        color: colorConfig.text,
        backgroundColor: colorConfig.enabled.bg,
        "&:hover": {
          backgroundColor: colorConfig.hover.bg,
        },
        "&:disabled": {
          backgroundColor: colorConfig.disabled.bg,
          color: colorConfig.disabled.text,
        },
        ...props.sx,
      }}
    >
      <span style={{ whiteSpace: "nowrap" }}>{children}</span>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadingIcon size={sizeConfig.loadingSize} />
        </Box>
      )}
    </Button>
  );
}
